import {
  DownloadOutlined,
  LoadingOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  Button,
  Progress,
  Table, Upload
} from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import allActions from '../../../actions';
import { showNotification } from '../../../actions/utilities/Notifications/NotificationActions';
import ConfirmModal from '../../../components/confirmModal/ConfirmModal';
import { formatMaterialNumber } from '../../../utils/DateAndNumberFunctions';
import { getCorrectTokenFromUser } from '../../../utils/token';
import CommonOperations from '../../MaterialDetails/summaryElements/SummaryComponents/Recommendations/CommonOperations';
import { createIOTAPayloadMassMRP, createIOTAPayloadMassMRPnoChanges } from '../../MaterialDetails/summaryElements/SummaryComponents/Recommendations/Generators';
import { tableColumns } from '../../MaterialDetails/summaryElements/SummaryComponents/Recommendations/RecommendationsData';
import { massUpdate } from '../utils/massUpdateGlobals';
import { updateSelectedMaterials } from '../utils/utils';
import {
  getDataSource, getRowClassName, objectConstructor,
} from './MassUpdateMrpHelpers';
import { configColumns, headers } from './MassUpdateMrpTableHelpers';
import MRPConfirm from './MRPConfirm';
import PlantMaterialSelector from './plantMaterialSelecor/PlantMaterialSelector';
import styles from './styles.module.scss';
import {
  checkIfMaterialIsAccordingToStrategy,
  downloadNewStockResults,
  generateExcelValues,
  getDeliveryTimePayload, getInitialValuesForMassUpdate, getSpecialRulePdTargetPlants,
  lockedColumns,
  readXLSX
} from './utils';

const MassUpdateMrp = ({ matIds, currentTab }) => {
  if (currentTab !== massUpdate.MRP) return null;
  const dispatch = useDispatch();
  const [materialData, setMaterialData] = useState([]);
  const user = useSelector((state) => state.authState.user);
  const plant = useSelector((state) => state.commonState.massUpdatePlant);
  const selectedMaterials = useSelector((state) => state.commonState.massUpdateMaterials || []);
  const dataSrc = useSelector((state) => state.commonState.dataSrc);
  const [loading, setLoading] = useState(false);
  const [relatedPlants, setRelatedPlants] = useState([]);
  const [materialsToUse, setMaterialsToUse] = useState([]);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [excludeList, setExcludeList] = useState([]);
  const [originalValues, setOriginalValues] = useState({});
  const [resetChanges, setResetChanges] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [delegateMenuVisible, setDelegateMenuVisible] = useState(false);

  const [ready, setReady] = useState(false);
  const [changes, setChanges] = useState({});
  const [oldValues, setOldValues] = useState({});
  const [showRevertButton, setShowRevertButton] = useState(false);
  const [displayTable, setDisplayTable] = useState(false);
  const [hasChangesToND, setHasChangesToND] = useState(null);
  const [hasChangesToPD, setHasChangesToPD] = useState(null);
  const [hasNDChangesModal, setHasNDChangesModal] = useState(false);
  const [groupChangeId, setGroupChangeId] = useState('');

  const [populateInputs, setPopulateInputs] = useState(false);
  const [calculateMonImp, setCalculateMonImp] = useState(false);

  const [revertNoMrpChanges, setRevertNoMrpChanges] = useState(false);

  const [fetchedPlant, setFetchedPlant] = useState(null);
  const [mrpsForRelatedPlants, setMrpsForRelatedPlants] = useState(null);

  const [revertedChangelog, setRevertedChangelog] = useState([]);

  const [recTableLayout, setRecTableLayout] = useState({});

  const [commonOperations, setCommonOperations] = useState({});

  const [sendingData, setSendingData] = useState('');
  const [sendingDataInfo, setSendingDataInfo] = useState('');

  const [excelValues, setExcelValues] = useState([]);
  const [fileList] = useState([]);

  const [showDescription, setShowDescription] = useState(false);

  const [hasFetchErrors, setHasErrors] = useState(false);

  const [displayedChanges, setDisplayedChanges] = useState(10);

  useEffect(() => {
    if (matIds?.ids?.length > 0) {
      if (matIds.plant) {
        dispatch(allActions.CommonActions.setMassUpdatePlant(matIds.plant.toString()));
      }
      // Updating selected materials if copied from material list
      updateSelectedMaterials(matIds.ids, dispatch);
    }
  }, []);

  const removeNonValidMaterials = (data) => {
    // Only use valid materials with material data matching this plant and materials
    const materialDatas = Object.values(data).map((obj) => obj.MATERIAL_TYPE_SAP_ID);
    const validMaterials = selectedMaterials.filter(
      (material) => materialDatas.some((id) => id?.toString() === material?.toString()),
    );
    return validMaterials;
  };

  const fetchMrpsForRelatedPlants = async (ids) => {
    // Fetching MRPs for related plants, as they might be required for delivery time calculation
    const mrpData = [];
    if (selectedMaterials.length > 50) {
      const chunkSize = 150;
      const arrayOfArrays = [];

      for (let i = 0; i < selectedMaterials.length; i += chunkSize) {
        const chunk = selectedMaterials.slice(i, i + chunkSize);
        arrayOfArrays.push(chunk);
      }

      await Promise.all(ids.map(async (id) => {
        await Promise.all(arrayOfArrays.map(async (arr) => {
          const data = await allActions.SapAPIActions.getMRPsWithFetch(
            [id], arr, getCorrectTokenFromUser(user)
          );
          mrpData.push(...data);
        }));
      }));
    } else {
      const mrpsForRelatedPlantsData = await allActions.SapAPIActions.getMRPsWithFetch(
        ids, selectedMaterials, getCorrectTokenFromUser(user)
      );
      mrpData.push(...mrpsForRelatedPlantsData);
    }
    setMrpsForRelatedPlants(mrpData);
  };

  const getMrpsForRelatedPlants = async (fetchedRelatedPlants) => {
    const relatedPlantsString = fetchedRelatedPlants.map((relPlant) => relPlant.Plant_ID);
    const specialRulePlants = getSpecialRulePdTargetPlants(plant);
    const allPlantsToFetch = [...new Set([...relatedPlantsString.map(String), ...specialRulePlants])];
    if (allPlantsToFetch && allPlantsToFetch.length > 0) {
      await fetchMrpsForRelatedPlants(allPlantsToFetch);
    }
  };

  const getMaterialDataForRelatedPlants = async (fetchedRelatedPlants, validMaterials) => {
    const additionalMaterials = [];
    fetchedRelatedPlants?.forEach((relatedPlant) => {
      validMaterials.forEach((material) => {
        additionalMaterials.push({
          plantID: relatedPlant?.Plant_ID?.toString(),
          materialID: material?.toString(),
        });
      });
    });
    if (additionalMaterials?.length > 0) {
      const relatedPlantsMatData = await allActions.MaterialDetailsActions.fetchMaterialsAsync(
        additionalMaterials, getCorrectTokenFromUser(user), dispatch
      );
      setMaterialData((prev) => ([...prev, ...relatedPlantsMatData]));
    }
  };

  const createKey = (p, m) => `${p}-${Number.parseInt(m, 10)}`;

  const updateOriginalValues = (values, errors) => {
    if (values) {
      Object.values(values).forEach((val) => {
        if (val?.errorMessage) {
          delete val.errorMessage;
        }
      });
    }
    if (errors?.length > 0) {
      errors.forEach((err) => {
        const key = createKey(plant, err.material);
        values[key].errorMessage = err.message;
      });
    }
    setOriginalValues(values);
  };

  useEffect(() => {
    if (hasFetchErrors) {
      setTableKey((k) => k + 1);
    }
  }, [originalValues, hasFetchErrors]);

  const handleRelatedPlants = async (relatedPlantsFetched, validMaterials) => {
    await getMaterialDataForRelatedPlants(relatedPlantsFetched, validMaterials);
    await getMrpsForRelatedPlants(relatedPlantsFetched);
  };

  const createCorrectDataStructure = async (mrpDataFetched, materialDataFetched, isReverting) => {
    const formattedData = objectConstructor(mrpDataFetched, materialDataFetched);
    const validMaterials = removeNonValidMaterials(formattedData);
    updateOriginalValues(formattedData);
    // Generating the excel values for the table
    setExcelValues(generateExcelValues({}, formattedData));
    setMaterialsToUse(validMaterials);
    // Setting the excluded materials for the table
    const setToExcluded = selectedMaterials.filter((id) => !validMaterials.includes(id));
    setExcludeList(setToExcluded);
    // Setting the fetched plant for the table
    setFetchedPlant(mrpDataFetched);
    // Setting the initial values for the table
    const newRecTableData = _.cloneDeep(tableColumns());
    const recTable = getInitialValuesForMassUpdate(
      materialDataFetched, newRecTableData, mrpDataFetched, plant
    );
    // Setting recommendations and current values to be used in the common operations dropdown
    setRecTableLayout(recTable);

    if (!isReverting) {
      const relPlants = await allActions.MaterialDetailsActions.fetchPlantRelationshipsAsync(
        plant, getCorrectTokenFromUser(user), dispatch
      );
      setRelatedPlants(relPlants);
      if (relPlants.length > 0) {
        setSendingData('Fetching related plants.');
        setSendingDataInfo('Collecting related-plant data needed for dependent updates.');
        await handleRelatedPlants(relPlants, validMaterials);
      }
    }
  };


  const createChunks = (list) => {
    /*
    This is needed for fetching MRP data from SAP API,
    but also to update the changelogs in the IOTA DB.
    As there is a limit on the length of the request
    (SAP API does this by doing "materialID eq 1000 or materialID eq 1001 or ...")
    After some testing, we've seen that 150 is a good limit.
    It could be slightly higher, but it might hit an error if all material ids
    in the request are long (6 characters or more).

    Not sure how to improve this without changing the SAP API.
    With that said, when using too many materials (300 or more), IOTA UI is very slow and laggy.

    The function is a simply batching function, that takes a list and splits it into chunks of 150.
    */
    const chunkSize = 150;
    const arrayOfArrays = [];

    const arrayToUse = list || selectedMaterials;

    for (let i = 0; i < arrayToUse.length; i += chunkSize) {
      const chunk = arrayToUse.slice(i, i + chunkSize);
      arrayOfArrays.push(chunk);
    }
    return arrayOfArrays;
  };

  const getLoadingProgress = () => {
    if (!loading) return 0;

    switch (sendingData) {
      case 'Fetching Material data.':
        return 20;
      case 'Fetching MRP data.':
        return 45;
      case 'Fetching related plants.':
        return 65;
      case 'Updating MRP data.':
        return 80;
      case 'Updating changelogs.':
        return 92;
      case 'Updating related plants.':
        return 96;
      default:
        return 10;
    }
  };

  const getMrpData = async () => {
    const mrpData = [];
    if (selectedMaterials.length > 150) {
      const arrayOfArrays = createChunks();

      await Promise.all(arrayOfArrays.map(async (arr) => {
        const data = await allActions.SapAPIActions.getMRPsWithFetch(
          [plant], arr, getCorrectTokenFromUser(user), dispatch
        );
        mrpData.push(...data);
      }));
    } else {
      const data = await allActions.SapAPIActions.getMRPsWithFetch(
        [plant], selectedMaterials, getCorrectTokenFromUser(user), dispatch
      );
      mrpData.push(...data);
    }

    return mrpData;
  };

  const retrieveMaterials = async (isReverting) => {
    setSendingData('Fetching Material data.');
    setSendingDataInfo('Loading selected materials for the chosen plant.');
    if (!isReverting) {
      setRelatedPlants([]);
    }
    setMaterialData([]);
    setLoading(true);
    setHasChanges(false);
    setDisplayTable(true);
    setHasChangesToND(null);
    setHasChangesToPD(null);
    if (!plant || !(selectedMaterials?.length > 0)) return;
    const plantMaterials = selectedMaterials.map((materialID) => ({
      plantID: plant,
      materialID,
    }));
    const matData = await allActions.MaterialDetailsActions.fetchMaterialsAsync(
      plantMaterials, getCorrectTokenFromUser(user), dispatch
    );
    setSendingData('Fetching MRP data.');
    setSendingDataInfo('Getting MRP values and preparing rows for the table.');
    const data = await getMrpData();
    setMaterialData((prev) => ([...prev, ...matData]));
    createCorrectDataStructure(data, matData, isReverting);
    setLoading(false);
  };

  useEffect(() => {
    /* Force rerender of table to refill initial values */
    if (resetChanges) {
      setTableKey((k) => k + 1);
    }
  }, [resetChanges]);

  const setBigDataIfManyItems = () => {
    retrieveMaterials();
  };

  const updateChangelogWithoutMRPChanges = async (
    monetaryImpactChanges, otherChanges, identifier
  ) => {
    // Creating the payload for the changelog without MRP changes
    const iotaPayload = createIOTAPayloadMassMRPnoChanges(monetaryImpactChanges, user);
    if (iotaPayload.length > 150) {
      // If the payload is too long, we split it into chunks of 150
      const arrayOfArrays = createChunks(iotaPayload);
      await Promise.all(arrayOfArrays.map(async (arr) => {
        dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(arr, false, identifier));
      }));
    } else {
      // If the payload is not too long, we send it in one request
      dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(
        iotaPayload, false, identifier
      ));
    }
    if (!otherChanges) {
      // If there are no other changes, we clear the monetary impact summary
      dispatch(allActions.CommonActions.clearMonetaryImpactSummary());
      setShowRevertButton(true);
      setResetChanges(true);
    }
    setLoading(false);
  };

  const updateSuccessful = async (isReverting, identifier) => {
    if (isReverting) {
      setShowRevertButton(false);
      if (revertedChangelog?.changelog?.length > 0) {
        const iotaPayload = createIOTAPayloadMassMRP(revertedChangelog, user);
        setGroupChangeId('');
        if (iotaPayload.length > 150) {
          const arrayOfArrays = createChunks(iotaPayload);
          await Promise.all(arrayOfArrays.map(async (arr) => {
            dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(
              arr, true, identifier
            ));
          }));
        } else {
          dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(
            iotaPayload, true, identifier
          ));
        }
      }
    } else {
      // The GroupChangeId identifier is used to group the changelogs in IOTA DB.
      // That way we can revert the whole batch of changes.
      setGroupChangeId(identifier);
      const iotaPayload = createIOTAPayloadMassMRP(changes, user);
      if (iotaPayload.length > 150) {
        const arrayOfArrays = createChunks(iotaPayload);
        await Promise.all(arrayOfArrays.map(async (arr) => {
          dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(
            arr, false, identifier
          ));
        }));
      } else {
        dispatch(allActions.MaterialDetailsActions.createMassMrpChangelog(
          iotaPayload, false, identifier
        ));
      }
      // Updating the delivery time and/or special procurement type
      // for related plants if requirements are met
      const deliveryTimePayload = getDeliveryTimePayload(
        plant, fetchedPlant, relatedPlants, mrpsForRelatedPlants, changes, materialData
      );
      if (deliveryTimePayload.to_Mrp.length > 0) {
        setSendingData('Updating related plants.');
        setSendingDataInfo('Applying updates to related plants where required.');
        // Need to send the payload as its own payload
        const err = await allActions.SapAPIActions.setMrpsAsync(
          deliveryTimePayload,
          getCorrectTokenFromUser(user),
          dispatch,
          false,
          plant,
          dataSrc?.matList,
        );
        if (err) {
          setLoading(false);
        }
      }
      setShowRevertButton(true);
    }
    setSendingData('');
    setSendingDataInfo('');
    // Retrieving the material data again to update the table with the new values
    retrieveMaterials(isReverting);
    setResetChanges(true);
    dispatch(showNotification({
      Type: 'success',
      Title: 'MRP data updated',
    }));
  };

  const resetAll = (includePlantMaterials = true) => {
    if (includePlantMaterials) {
      dispatch(allActions.CommonActions.setMassUpdatePlant(null));
      dispatch(allActions.CommonActions.setMassUpdateMaterials([]));
    }
    setHasErrors(false);
    setMaterialData([]);
    setRelatedPlants([]);
    setMaterialsToUse([]);
    setResetChanges(true);
    setError(false);
    setShowModal(false);
    setExcludeList([]);
    setOriginalValues({});
    setTableKey(0);
    setReady(false);
    setChanges({});
    setDisplayTable(false);
  };

  useEffect(() => {
    if (materialsToUse.length > 0) {
      resetAll(false);
    }
  }, [plant, selectedMaterials]);

  const previewChanges = () => {
    setReady(true);
  };

  useEffect(() => {
    if (Object.keys(changes).length > 0) {
      const NDChanges = [];
      const PDChanges = [];
      const { changelog } = changes;
      // Here we are checking for PD or ND changes,
      // as we should display a modal to confirm these changes
      changelog.forEach((change) => {
        if (change.changeType.includes('MRPType') && change.oldValue !== 'ND' && change.newValue === 'ND' && !excludeList.find((material) => change.MaterialNumber.includes(material))) {
          const material = originalValues?.[createKey(change.Plant, change.MaterialNumber)];
          if (material?.docCount > 0 || material?.TOT_STOCK > 0) {
            NDChanges.push(change);
          }
        } else if (change.changeType.includes('MRPType') && change.oldValue === 'ND' && (change.newValue === 'PD' || change.newValue === 'V1') && !excludeList.find((material) => change.MaterialNumber.includes(material))) {
          // Check if material is not according to strategy
          const material = materialData.find(
            (m) => m.MATERIAL_TYPE_SAP_ID === formatMaterialNumber(change.MaterialNumber)
              && m.PLANT_FACILITY_SAP_ID === change.Plant
          );
          const orgMaterial = originalValues?.[createKey(change.Plant, change.MaterialNumber)];
          if (material && checkIfMaterialIsAccordingToStrategy(material, orgMaterial)) {
            PDChanges.push(change);
          }
        }
      });
      if (NDChanges.length > 0 || PDChanges.length > 0) {
        if (PDChanges.length > 0) {
          setHasChangesToPD(PDChanges);
        }
        if (NDChanges.length > 0) {
          setHasChangesToND(NDChanges);
        }
        setHasNDChangesModal(true);
        setShowModal(false);
      } else setShowModal(true);
    }
  }, [changes, ready]);

  const submitChanges = async () => {
    setShowModal(false);
    setLoading(true);
    setSendingData('Updating MRP data.');
    setSendingDataInfo('Submitting your edited MRP values to SAP.');
    setHasErrors(false);

    // eslint-disable-next-line camelcase
    const { MrpBatch, to_Mrp, monetaryImpacts } = changes;
    // Creating the payload for the reverted changes
    const revertedChanges = changes?.changelog?.map(
      (change) => ({ ...change, newValue: change.oldValue, oldValue: change.newValue }),
    );
    if (revertedChanges.length > 0) {
      setRevertedChangelog({ ...changes, changelog: revertedChanges });
    }
    // Creating the payload for the MRP changes
    const changeObject = { MrpBatch, to_Mrp };
    const noMrpChanges = [];
    const mrpChanges = [];
    // Separating the MRP changes from the non-MRP changes
    monetaryImpacts.forEach((impact) => {
      const change = to_Mrp.find((i) => i.MaterialNumber.includes(impact.material));
      if (change) {
        mrpChanges.push(impact);
      } else {
        noMrpChanges.push(impact);
      }
    });
    // Creating a unique identifier for the changelogs
    const identifier = uuid();

    if (mrpChanges.length > 0 || to_Mrp.length > 0) {
      // Sending the MRP changes to SAP API
      const errors = await allActions.SapAPIActions.setMrpsAsync(
        changeObject,
        getCorrectTokenFromUser(user),
        dispatch,
        false,
        plant,
        dataSrc?.matList,
      );
      if (errors) {
        // Updating the original values to show the errors in the table
        updateOriginalValues(originalValues, errors);
        setHasErrors(true);
        setSendingData('');
        setSendingDataInfo('');
        setLoading(false);
      } else {
        // Updating the changelogs in IOTA DB
        setHasErrors(false);
        setSendingData('Updating changelogs.');
        setSendingDataInfo('Saving the change history for this update batch.');
        updateSuccessful(false, identifier);
      }
    } else if (noMrpChanges.length > 0) {
      setRevertNoMrpChanges(true);
      setSendingData('Updating changelogs.');
      setSendingDataInfo('Saving non-MRP updates to the changelog.');
    }
    // Updating the changelogs in IOTA DB without MRP changes
    // (this can be only monetary impact changes)
    if (noMrpChanges.length > 0) {
      updateChangelogWithoutMRPChanges(noMrpChanges, mrpChanges.length > 0, identifier);
    }
  };

  const submitRevert = async () => {
    setShowRevertButton(false);
    if (!revertNoMrpChanges) {
      setLoading(true);
      setSendingData('Updating MRP data.');
      setSendingDataInfo('Reverting submitted values to their previous state.');
      // Sending the reverted changes to SAP API
      const errors = await allActions.SapAPIActions.setMrpsAsync(
        oldValues,
        getCorrectTokenFromUser(user),
        dispatch,
        false,
        plant,
        dataSrc?.matList,
      );
      if (errors) {
        updateOriginalValues(originalValues, errors);
        setHasErrors(true);
        setSendingData('');
        setSendingDataInfo('');
        setLoading(false);
      } else {
        // Updating the changelogs in IOTA DB (with reverting being true)
        setHasErrors(false);
        updateSuccessful(true);
      }
    }
    // Reverting the changelogs in IOTA DB
    dispatch(allActions.MaterialDetailsActions.revertChangeLog({ id: groupChangeId }));
  };

  const resetPopulateInputs = () => {
    setPopulateInputs(false);
  };

  const resetCalculateMonImp = () => {
    setCalculateMonImp(false);
  };

  const notValidData = () => {
    const { monetaryImpacts } = changes;
    if (!changes.to_Mrp) return true;
    const hasError = monetaryImpacts.find((i) => i.error === true);
    if (hasError) return true;
    return false;
  };

  const updateSelected = (selectedMats) => {
    updateSelectedMaterials(selectedMats, dispatch);
  };

  const getCorrectValues = (commonValues, key) => {
    const object = { ...recTableLayout[key].current };
    headers.forEach((header) => {
      if (commonValues[header.type] === 'KEEP') recTableLayout[key][header.type];
      else if (commonValues[header.type]) object[header.type] = commonValues[header.type];
      else {
        object[header.type] = '';
      }
    });
    return object;
  };

  // Getting the recommended or current values for the common operations dropdown
  const getRecOrCurValues = (value, type, key) => {
    const object = { ...recTableLayout[key].current };
    headers.forEach((header) => {
      object[header.type] = value[type][header.type];
    });
    return object;
  };

  const updateChanges = (commonObject) => {
    if (commonObject.type) {
      const temp = {};
      const { type } = commonObject;
      materialsToUse.forEach((mat) => {
        if (excludeList.includes(mat)) return null;
        const key = createKey(plant, mat);
        const matData = commonObject.data[key];
        temp[key] = getRecOrCurValues(matData, type, key);
        return null;
      });
      setCommonOperations(temp);
    } else {
      const temp = {};
      materialsToUse.forEach((mat) => {
        if (excludeList.includes(mat)) return null;
        const key = createKey(plant, mat);
        temp[key] = getCorrectValues(commonObject.data, key);
        return null;
      });
      setCommonOperations(temp);
    }
  };

  const [dropdownValues, setDropdownValues] = useState({});
  const [importedValues, setImportedValues] = useState([]);

  const updateExcelValues = (values, monImps) => {
    const copy = JSON.parse(JSON.stringify(originalValues));
    const rows = generateExcelValues(values, copy, monImps);
    setExcelValues(rows);
  };

  const uploadValuesFromImport = (rows) => {
    // Updating the table with the new values from the excel file
    setExcludeList([]);
    const inputs = {};
    const monImps = [];
    const tempExcludeList = [];

    // Checking if the list is wrong
    // (Checking if material ids and plantsare correct,
    // and that its the same length as the original values)
    let wrongList = false;

    rows.forEach((row) => {
      const key = createKey(row.PLANT_FACILITY_SAP_ID, row.MATERIAL_TYPE_SAP_ID);
      if (row.selected !== 'Selected') tempExcludeList.push(row.MATERIAL_TYPE_SAP_ID);
      delete row.selected;
      Object.entries(row).forEach((value) => {
        if (!originalValues[key] || Object.keys(originalValues).length !== rows.length) {
          wrongList = true;
        }
        if (originalValues[key] && row[value[0]] !== originalValues[key][value[0]]) {
          if (!lockedColumns.includes(value[0])) {
            inputs[key] = {
              ...inputs[key],
              [value[0]]: typeof value[1] === 'undefined' ? '' : value[1]
            };
          }
        }
      });
      if (originalValues[key] && (row.monetaryImpact !== '0' || row.monetaryImpactType !== 'No impact')) {
        // Creating the new impact object
        const newImpact = {
          impact: typeof row.monetaryImpact === 'undefined' ? '0' : row.monetaryImpact,
          monImpType: row.monetaryImpactType,
          plant: row.PLANT_FACILITY_SAP_ID,
          material: row.MATERIAL_TYPE_SAP_ID,
          edited: true,
          key,
          error: (row.monetaryImpact.toString() === '0' && row.monetaryImpactType !== 'No impact') || (row.monetaryImpact.toString() !== '0' && row.monetaryImpactType === 'No impact'),
        };
        monImps.push(newImpact);
      }
    });

    if (!wrongList) {
      // Setting the imported values and excluded materials
      setImportedValues({ inputs, monImps });
      setExcludeList(tempExcludeList);
      if (Object.keys(inputs).length > 0 || monImps.length > 0) {
        // Setting the has changes flag to true if there are any changes
        setHasChanges(Object.keys(inputs).length > 0 || monImps.length > 0);
      }
      if (Object.keys(inputs).length > 0 || monImps.length > 0) {
        dispatch(showNotification(
          {
            Type: 'success',
            Title: 'MRP data uploaded from excel',
          },
        ));
      }
    } else {
      dispatch(showNotification(
        {
          Type: 'warning',
          Title: 'There is a difference between mass update data and excel data. Make sure you upload the correct file.',
        },
      ));
    }
    setLoading(false);
  };

  const updatePlant = (p) => {
    dispatch(allActions.CommonActions.setMassUpdatePlant(p));
  };

  // The modal for confirming changes to MRP type
  // We show warnings if changing to ND from PD/V1 or PD/V1 to ND
  return (
    <>
      <ConfirmModal
        style={{
          minWidth: '30vw', minHeight: '80vh', maxWidth: '80vw', maxHeight: '80vh',
        }}
        title="Changes to MRP Type"
        visible={hasNDChangesModal}
        onOK={() => {
          setHasNDChangesModal(false);
          setHasChangesToND(null);
          setHasChangesToPD(null);
          setShowModal(true);
        }}
        onCancel={() => {
          setHasChangesToND(null);
          setHasChangesToPD(null);
          setHasNDChangesModal(false);
          setChanges({});
        }}
        message={(
          <div className={styles.NDChanges}>
            {hasChangesToND && (
            <>
              <h4>Warning: Materials with open docs or total stock has changed MRP type to ND!</h4>
              {hasChangesToND.slice(0, displayedChanges).map((change) => (
                <div key={`${change.Plant}-${change.MaterialNumber}`}>
                  Plant:
                  {change.Plant}
                  {' '}
                  - Material:
                  {' '}
                  {change.MaterialNumber}
                </div>
              ))}
              {hasChangesToND.length > displayedChanges && (
                <Button
                  type="link"
                  onClick={() => setDisplayedChanges((prev) => prev + 10)}
                  style={{ marginTop: '8px' }}
                >
                  Show More
                </Button>
              )}
            </>
            )}
            {hasChangesToPD && (
            <>
              <h4>Warning: Materials not according to strategy has changed MRP type to PD/V1!</h4>
              {hasChangesToPD.map((change) => (
                <div>
                  Plant:
                  {change.Plant}
                  {' '}
                  - Material:
                  {' '}
                  {formatMaterialNumber(change.MaterialNumber)}
                </div>
              ))}
            </>
            )}
            <div style={{ marginTop: '2rem' }}>Click confirm to continue.</div>
          </div>
        )}
      />
      <ConfirmModal
        style={{
          minWidth: '80vw', minHeight: '80vh', maxWidth: '80vw', maxHeight: '80vh',
        }}
        title="Confirm changes"
        visible={showModal}
        onOK={submitChanges}
        okButtonDisabled={notValidData()}
        onCancel={() => {
          setShowModal(false);
          setChanges({});
        }}
        message={(
          <MRPConfirm
            originalValues={originalValues}
            changes={changes}
            excludeList={excludeList}
          />
        )}
      />
      <div className={styles.massUpdateMrpContainer}>
        <PlantMaterialSelector
          plant={plant}
          updatePlant={(p) => updatePlant(p)}
          materials={selectedMaterials}
          setMaterials={updateSelected}
          onOK={setBigDataIfManyItems}
          error={error}
          setError={setError}
          resetAll={resetAll}
          validMaterials={materialsToUse}
        />
        <div className={styles.tableContainer}>
          {selectedMaterials?.length > 150 && loading && (
            <div className={styles.loadingView}>
              <div className={styles.loadingContent}>
                <div className={styles.loadingCard}>
                  <h1 style={{ fontWeight: '700' }} className={styles.loadingText}>
                    <LoadingOutlined spin />
                    {' '}
                    {sendingData}
                  </h1>
                  <div className={styles.loadingInfoText}>
                    {sendingDataInfo}
                  </div>
                  <Progress
                    percent={getLoadingProgress()}
                    showInfo={false}
                    size="small"
                    status="active"
                    className={styles.loadingProgress}
                  />
                </div>
              </div>
            </div>
          )}

          <Table
            key={tableKey}
            dataSource={displayTable ? getDataSource(selectedMaterials) : []}
            rowClassName={(v, i) => getRowClassName(i, v, materialsToUse)}
            className={styles.table}
            showSorterTooltip={false}
            columns={configColumns(
              materialData, loading, excludeList,
              setExcludeList, plant, materialsToUse,
              selectedMaterials, originalValues, resetChanges,
              setResetChanges, ready, setReady,
              setChanges, setOldValues, setHasChanges,
              populateInputs, resetPopulateInputs,
              calculateMonImp, resetCalculateMonImp,
              commonOperations, updateExcelValues,
              setDropdownValues, importedValues,
              setImportedValues, showDescription,
              setShowDescription, hasFetchErrors
            )}
            size="small"
            scroll={{ x: showDescription ? 2100 : 1500, y: 398 }}
            pagination={false}
            footer={() => (

              <div className={styles.buttonRow}>
                <Button
                  style={{ display: showRevertButton ? 'inherit' : 'none' }}
                  className="revertButton"
                  onClick={submitRevert}
                >
                  Undo submitted change
                </Button>
                <div className={styles.extraButtons}>
                  <Button
                    className="secondary"
                    onClick={() => {
                      downloadNewStockResults(excelValues, excludeList, dropdownValues);
                    }}
                    disabled={Object.keys(originalValues).length === 0}
                  >
                    <DownloadOutlined />
                    Download MRP data
                  </Button>
                  <Upload
                    accept=".xlsx"
                    fileList={fileList}
                    beforeUpload={(file) => {
                      const reader = new FileReader();
                      reader.readAsArrayBuffer(file);
                      reader.onload = () => {
                        const buffer = reader.result;
                        readXLSX(buffer, uploadValuesFromImport);
                      };
                      return false;
                    }}
                    maxCount={1}
                    className={styles.uploadBtn}
                  >
                    <Button className="secondary" disabled={Object.keys(originalValues).length === 0}>
                      <UploadOutlined />
                      Upload MRP data
                    </Button>
                  </Upload>
                  <CommonOperations
                    delegateMenuVisible={delegateMenuVisible}
                    setDelegateMenuVisible={setDelegateMenuVisible}
                    recTableData={recTableLayout}
                    setRecTableData={updateChanges}
                    isMassUpdate
                  />
                  <Button
                    className="secondary"
                    onClick={() => setPopulateInputs(true)}
                  >
                    Populate inputs
                  </Button>
                  <Button
                    className="secondary"
                    onClick={() => setCalculateMonImp(true)}
                  >
                    Calculate Mon Imp
                  </Button>
                  <Button
                    type="secondary"
                    onClick={() => {
                      setResetChanges(true);
                      setHasChanges(false);
                    }}
                    disabled={!hasChanges}
                  >
                    Reset all changes
                  </Button>
                  <Button
                    disabled={!hasChanges}
                    type="primary"
                    onClick={previewChanges}
                  >
                    Submit
                  </Button>
                </div>
              </div>

            )}
          />
        </div>
      </div>
    </>
  );
};

export default MassUpdateMrp;

MassUpdateMrp.propTypes = {
  currentTab: PropTypes.number.isRequired,
  matIds: PropTypes.shape({
    ids: PropTypes.arrayOf(PropTypes.string),
    plant: PropTypes.string,
  }),
};

MassUpdateMrp.defaultProps = {
  matIds: PropTypes.shape({
    ids: [],
    plant: '',
  }),
};
